"use client";

import React, { useEffect, useState, useMemo } from "react";
import overlay from "../../../../css/components/overlaymodal-animations.module.css";
import Button from "../../../../components/Button";
import styles from "../css/usageModal.module.css";
import { fetchStockUsage } from "../../../../api/Inventory/StockRequestApi.js";

export default function UsageModal({ title = "Average stock usage", sku, branch, onClose }) {
  const [usageRows, setUsageRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!sku) return;
      try {
        setLoading(true);
        setError("");
        const rows = await fetchStockUsage(sku, branch);
        if (!cancelled) {
          setUsageRows(Array.isArray(rows) ? rows : []);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load stock usage", err);
          setError("Failed to load stock usage");
          setUsageRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sku, branch]);

  const { data, labels, maxPositive, maxNegative, baseline } = useMemo(() => {
    if (!usageRows || !usageRows.length) {
      const zeroData = [0, 0, 0, 0];
      return {
        data: zeroData,
        labels: ["Week 1", "Week 2", "Week 3", "Week 4"],
        maxPositive: 1,
        maxNegative: 1,
        baseline: 100 // pixels from top
      };
    }
    const safeRows = usageRows.slice(0, 4);
    const values = safeRows.map((r) => Number(r.units_used) || 0);
    const lbls = safeRows.map((r) => r.week_label || "");
    
    const positiveValues = values.filter(v => v > 0);
    const negativeValues = values.filter(v => v < 0).map(v => Math.abs(v));
    
    const maxPositive = Math.max(...positiveValues, 1);
    const maxNegative = Math.max(...negativeValues, 1);
    
    // Calculate baseline position (50% for balanced display, adjusted for data)
    const positiveRatio = maxPositive / (maxPositive + maxNegative);
    const baseline = 50 + (positiveRatio - 0.5) * 30; // Adjust by ±15% max
    
    return {
      data: values,
      labels: lbls,
      maxPositive,
      maxNegative,
      baseline
    };
  }, [usageRows]);

  return (
    <div className={`${overlay.modalOverlay} ${styles.topOverlay}`} onClick={onClose}>
      <div className={`${overlay.modalContent} ${styles.container} ${styles.contentVisible}`} onClick={(e) => e.stopPropagation()}>
        <button className={overlay.closeButton} onClick={onClose} aria-label="Close">×</button>
        <h2 className={styles.title}>{title}</h2>
        <h3 className={styles.subTitle}>Weekly Summary</h3>

        {loading && (
          <div className={styles.loading}>
            <span>Loading usage data...</span>
          </div>
        )}

        {error && (
          <div className={styles.error}>
            <span>{error}</span>
          </div>
        )}

        {!loading && !error && (
          <>
            <div className={styles.legend}>
              <div className={styles.legendDot} />
              <span>Units Used</span>
            </div>

            <div className={styles.chart}>
              {/* Chart bars container with baseline */}
              <div className={styles.barsContainer}>
                {data.map((v, i) => {
                  const isNegative = v < 0;
                  const absoluteValue = Math.abs(v);
                  const maxValue = isNegative ? maxNegative : maxPositive;
                  const barHeight = (absoluteValue / maxValue) * 60; // Max 60px height for each direction
                  
                  return (
                    <div key={i} className={styles.barColumn}>
                      {/* Positive bar - goes up from baseline */}
                      {!isNegative && v > 0 && (
                        <div
                          className={`${styles.bar} ${styles.positiveBar}`}
                          style={{
                            height: `${barHeight}px`,
                            marginBottom: '2px' // Small gap from baseline
                          }}
                          title={`${v}`}
                        >
                          <span className={styles.barLabel}>{v}</span>
                        </div>
                      )}
                      
                      {/* Negative bar - goes down from baseline */}
                      {isNegative && v < 0 && (
                        <div
                          className={`${styles.bar} ${styles.negativeBar}`}
                          style={{
                            height: `${barHeight}px`,
                            marginTop: '2px' // Small gap from baseline
                          }}
                          title={`${v}`}
                        >
                          <span className={`${styles.barLabel} ${styles.negativeBarLabel}`}>{v}</span>
                        </div>
                      )}
                      
                      {/* Zero value - show minimal bar */}
                      {v === 0 && (
                        <div className={styles.zeroBar}>
                          <span className={styles.barLabel}>0</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {/* Week labels at the bottom */}
              <div className={styles.weekLabels}>
                {labels.map((label, i) => (
                  <div key={i} className={styles.weekLabel}>
                    {label || `Week ${i + 1}`}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        <div className={styles.footer}>
          <Button variant="primary" onClick={onClose}>Back</Button>
        </div>
      </div>
    </div>
  );
}
